// System dependencies

import CryptoJS from 'crypto-js';
import bcrypt from 'bcryptjs';
import { v4 as uuidv4 } from 'uuid';

// Application dependencies

import { AppDispatch } from '@/redux/store';
import { AuthAal, AuthChallengeMode, AuthCookieData, AuthFields, AuthProviders, AuthResponse, AuthTotp, AuthTotpStatus, AuthWaitingStatus, AuthWaitingType } from '@/_auth/interfaces/auth.interfaces';
import { authResetSession, authResetUser } from '@/_auth/redux/auth/auth.slices';
import { devConsole } from '@/lib/devConsole';
import { formatErrorMessage } from '@/lib/functions';
import { authClient } from '@/_auth/utils/auth/client';

// NAMESPACE

const NAMESPACE = 'auth.functions';

/**
 * Constructs a TOTP URI for use in generating QR codes for authentication apps.
 *
 * @param {string} secret - The shared secret key used for generating TOTP codes.
 * @param {string} issuer - The name of the application or service issuing the TOTP. This will be displayed in the authenticator app.
 * @param {string} accountName - The account name or identifier for the user (e.g., email address). This is displayed alongside the issuer
 * in the authenticator app.
 * @returns {string} The TOTP URI formatted for use in QR code generation.
 *
 */

export const buildTotpUri = (secret: string, issuer: string, accountName: string): string => {
  return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(issuer)}`;
};

/**
 * Decrypts an AES-256 encrypted string back into a CookieData object.
 *
 * @param {string} encryptedData - The AES-256 encrypted string to be decrypted.
 * @param {string} secretKey - The secret key used for decryption.
 * @returns {AuthCookieData} The decrypted CookieData object.
 * @throws Will throw an error if decryption fails or the secret key is incorrect.
 */

export const decryptCookie = (encryptedData: string, secretKey: string): AuthCookieData => {
  try {
    // Decrypt the string.

    const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);

    // Convert decrypted bytes to a string

    const jsonString = bytes.toString(CryptoJS.enc.Utf8);

    // Make sure we have a return.

    if (!jsonString) {
      throw new Error('Decryption resulted in empty data. Check the secret key.');
    }

    // Parse JSON string back to a CookieData object

    return JSON.parse(jsonString) as AuthCookieData;
  } catch (error) {
    // Handle any unexpected error.

    devConsole.error(NAMESPACE, `decryptCookie`, `Failed to decrypt cookie: ${formatErrorMessage(error)}`);
    throw new Error('Decryption failed. Ensure the secret key and encrypted data are valid.');
  }
};

/**
 * Encrypts a CookieData object into a secure AES-256 encrypted string.
 *
 * @param {AuthCookieData} data - The CookieData object to be encrypted.
 * @param {string} secretKey - The secret key used for encryption.
 * @returns {string} The AES-256 encrypted string.
 * @throws Will throw an error if encryption fails.
 */

export const encryptCookie = (data: AuthCookieData, secretKey: string): string => {
  try {
    // Convert data object to JSON string.

    const jsonString = JSON.stringify(data);

    // Encrypt the JSON string.

    return CryptoJS.AES.encrypt(jsonString, secretKey).toString();
  } catch (error) {
    // Handle any unexpected error.

    devConsole.error(NAMESPACE, `encryptCookie`, `Failed to encrypt cookie: ${formatErrorMessage(error)}`);
    throw new Error('Encryption failed. Ensure the secret key and data are valid.');
  }
};

/**
 * Generates a 6-digit OTP and hashes it securely.
 *
 * @returns {Promise<{ otp: string; hashedOtp: string }>} The plain OTP and its hashed value.
 */

export const generateAndHashOtp = async (): Promise<{
  otp: string;
  hashedOtp: string;
}> => {
  // Generate a random 6-digit OTP

  const otp = Math.floor(100000 + Math.random() * 900000).toString();

  // Hash the OTP using bcrypt

  const salt = await bcrypt.genSalt(10);
  const hashedOtp = await bcrypt.hash(otp, salt);

  return { otp, hashedOtp };
};

/**
 * Generates a HMAC SHA256 signature for the provided payload.
 *
 * @param expiresAt - The expiry timestamp.
 * @param issuedAt - The issued timestamp.
 * @param mfaStatus - The MFA status (e.g., "verified").
 * @param secretKey - The secret key used for signing.
 * @param sessionId - The unique session ID.
 * @param userId - The ID of the user.
 * @returns A Base64 encoded HMAC signature as a string.
 */

export const generateSignature = (expiresAt: string, issuedAt: string, mfaStatus: string, secretKey: string, sessionId: string, userId: string): string => {
  const payload = `${userId}:${sessionId}:${issuedAt}:${expiresAt}:${mfaStatus}`;
  return CryptoJS.HmacSHA256(payload, secretKey).toString(CryptoJS.enc.Base64);
};

/**
 * Generates a unique friendly name for use with the TOTP enroll function. It takes the prefix (defaults to "TOTP") and adds a the first
 * segment of a full UUID from uuid.v4.
 *
 * @param {string} prefix - The prefix string from the calling component with a default of TOTP is not provided.
 */

export const generateFriendlyName = (prefix: string = 'TOTP') => {
  // Generate a UUID and take a portion of it
  const shortUuid = uuidv4().split('-')[0]; // First segment of the UUID
  return `${prefix}-${shortUuid}`;
};

/**
 * Retrieves the user's current Authentication Assurance Level (AAL) from the auth API.
 *
 * This function calls `authClient.auth.mfa.getAuthenticatorAssuranceLevel()` to determine the user's AAL.
 *
 * AAL Levels:
 * - `aal0` → Unauthenticated (or no AAL data available)
 * - `aal1` → Single-factor authentication (Email/Password, OAuth, Email OTP)
 * - `aal2` → Multi-factor authentication (TOTP/WebAuthn verified)
 *
 * @returns {Promise<AuthAal>} The user's AAL level (`aal0`, `aal1`, or `aal2`).
 *
 */

export const getAal = async (): Promise<AuthAal> => {
  // Fetch AAL from the local auth API (client-side).

  const { data: aalData } = await authClient.auth.mfa.getAuthenticatorAssuranceLevel();

  // If no currentLevel is available, default to AAL0 (unauthenticated).

  return aalData?.currentLevel ? (aalData.currentLevel as AuthAal) : AuthAal.Aal0;
};

/**
 * Returns a user-friendly name for the given AuthWaitingStatus.
 *
 * @param {AuthWaitingStatus | null} waitingStatus - The waiting status.
 * @returns {string} - A user-friendly name for the waiting status.
 *
 */

export const getWaitingStatus = (waitingStatus: AuthWaitingStatus | null): string => {
  switch (waitingStatus) {
    case AuthWaitingStatus.Accepted:
      return 'Accepted';
    case AuthWaitingStatus.Error:
      return 'Error';
    case AuthWaitingStatus.Waiting:
      return 'Waiting';
    default:
      return 'Unknown Status';
  }
};

/**
 * Returns a user-friendly name for the given AuthWaitingType.
 *
 * @param {AuthWaitingType | null} waitingType - The waiting type.
 * @returns {string} - A user-friendly name for the waiting type.
 *
 */

export const getWaitingType = (waitingType: AuthWaitingType | null): string => {
  switch (waitingType) {
    case AuthWaitingType.Oauth_entra:
      return 'Microsoft Entra Sign-In';
    case AuthWaitingType.Oauth_google:
      return 'Google Sign-In';
    case AuthWaitingType.Recovery:
      return 'Password Recovery';
    case AuthWaitingType.Signup:
      return 'Account Sign-Up';
    default:
      return 'Unknown Action';
  }
};

/**
 * Maps the status string to an AuthTotpStatus enum value and defaults to none if it can't find one.
 *
 * @param {string | undefined} status - the status string to map to the AuthTotpStatus enum.
 * @returns {AuthTotpStatus} - The corresponding AuthTotpStatus enum or the none default if one is not found.
 */

export const mapFactorStatusToEnum = (status: string | undefined) => {
  return Object.values(AuthTotpStatus).includes(status as AuthTotpStatus) ? (status as AuthTotpStatus) : AuthTotpStatus.None;
};

/**
 * Maps the provider string to an AuthProviders enum value and defaults to email if it can't find one.
 *
 * @param {string | undefined} provider - the provider string to map to the AuthProviders enum.
 * @returns {AuthProviders} - The corresponding AuthProviders enum or the email default if one is not found.
 */

export const mapProviderToEnum = (provider: string | undefined): AuthProviders => {
  if (provider === 'microsoft' || provider === 'entra') {
    return AuthProviders.Microsoft;
  }

  return Object.values(AuthProviders).includes(provider as AuthProviders) ? (provider as AuthProviders) : AuthProviders.Email;
};

/**
 * Maps the waitingStatus string to an AuthWaitingStatus enum value and defaults to unknownn if it can't find one.
 *
 * @param {string | undefined} waitingStatus - the waitingStatus string to map to the AuthWaitingStatus enum.
 * @returns {AuthWaitingStatus} - The corresponding AuthWaitingStatus enum or the unknown default if one is not found.
 */

export const mapWaitingStatusToEnum = (waitingStatus: string | undefined): AuthWaitingStatus => {
  return Object.values(AuthWaitingStatus).includes(waitingStatus as AuthWaitingStatus) ? (waitingStatus as AuthWaitingStatus) : AuthWaitingStatus.Unknown;
};

/**
 * Maps the waitingType string to an AuthWaitingType enum value and defaults to unknownn if it can't find one.
 *
 * @param {string | undefined} waitingType - the waitingType string to map to the AuthWaitingType enum.
 * @returns {AuthWaitingType} - The corresponding AuthWaitingType enum or the unknown default if one is not found.
 */

export const mapWaitingTypeToEnum = (waitingType: string | undefined): AuthWaitingType => {
  return Object.values(AuthWaitingType).includes(waitingType as AuthWaitingType) ? (waitingType as AuthWaitingType) : AuthWaitingType.Unknown;
};

/**
 * Perform auth reset by dispatching necessary Redux actions.
 *
 * @param dispatch Redux dispatch function
 */

export const resetAuthState = async (dispatch: AppDispatch) => {
  dispatch(authResetSession());
  dispatch(authResetUser());
};

/**
 * Sets up a TOTP challenge for a verified MFA factor.
 *
 * @returns {Promise<AuthResponse & { totp: AuthTotp | null }>} A response object with TOTP data.
 */

export const setupTotpChallenge = async (): Promise<AuthResponse & { totp: AuthTotp | null }> => {
  // Try and setup the challenge for a verfied MFA factor.

  try {
    // Try and get the factors for the current user.

    const { data: factorData, error: factorError } = await authClient.auth.mfa.listFactors();

    // See if there is an error and, if so, throw an error so it is caught by the catch block.

    if (factorError) {
      throw new Error(`Failed to list MFA factors: ${formatErrorMessage(factorError)}`);
    }

    // Try and find a verified factor.

    const verifiedFactor = factorData?.all?.find((factor) => factor[AuthFields.Status] === AuthTotpStatus.Verified && factor[AuthFields.FactorType] === AuthChallengeMode.totp);

    // Check for no verified factor and, if so, throw an error so it is caught by the catch block.

    if (!verifiedFactor) {
      return {
        [AuthFields.Id]: null,
        [AuthFields.Message]: 'No verified TOTP factor found.',
        [AuthFields.Status]: 404,
        totp: null,
      };
    }

    // Try and create a challenge for the verified factor.

    const { data: challengeData, error: challengeError } = await authClient.auth.mfa.challenge({
      factorId: verifiedFactor.id,
    });

    // See whether we have a challenge error or no challenge data and, if so, throw an error so it is caught by the catch block.

    if (challengeError || !challengeData) {
      throw new Error(`Failed to create TOTP challenge: ${formatErrorMessage(challengeError) || 'Unknown error'}`);
    }

    // Return the challenge details

    return {
      [AuthFields.Id]: verifiedFactor.id,
      [AuthFields.Message]: 'TOTP challenge successfully created.',
      [AuthFields.Status]: 200,
      [AuthFields.Totp]: {
        [AuthFields.ChallengeId]: challengeData.id,
        [AuthFields.Id]: verifiedFactor.id,
        [AuthFields.Secret]: null,
        [AuthFields.Uri]: null,
      },
    };
  } catch (error) {
    // Handle any error by returning the message.

    return {
      [AuthFields.Id]: null,
      [AuthFields.Message]: formatErrorMessage(error) || 'An unexpected error occurred.',
      [AuthFields.Status]: 500,
      [AuthFields.Totp]: null,
    };
  }
};
